import React, { useState, useContext, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useWindowSize } from 'react-use';
import { demoPagesMenu } from '../../menu';
import { DropdownItem, DropdownMenu } from '../../components/bootstrap/Dropdown';
import Button from '../../components/bootstrap/Button';
import useDarkMode from '../../hooks/useDarkMode';
import Collapse from '../../components/bootstrap/Collapse';
import { NavigationLine } from '../Navigation/Navigation';
import Icon from '../../components/icon/Icon';
import useNavigationItemHandle from '../../hooks/useNavigationItemHandle';
import AuthContext from '../../contexts/authContext';
import ThemeContext from '../../contexts/themeContext';
import { UserType } from '../../common/data/dummyRolesData';
import { useOrganization } from '../../contexts/organizationContext';
import profileService from '../../services/api/profileService';
import { displayName } from '../../helpers/helpers';

const User = () => {
	const { width } = useWindowSize();
	const { setAsideStatus } = useContext(ThemeContext);
	const { userData, logout } = useContext(AuthContext);
	const { selectedOrganization } = useOrganization();

	const navigate = useNavigate();
	const handleItem = useNavigationItemHandle();
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();

	const [collapseStatus, setCollapseStatus] = useState<boolean>(false);
	const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
	const [imgError, setImgError] = useState(false);

	const { t } = useTranslation(['menu', 'common']);

	// Fetch avatar URL based on user role
	useEffect(() => {
		if (userData?.role === UserType.FREELANCE && userData?.id) {
			profileService
				.getFreelanceProfile()
				.then((profile) => {
					const url = profile.profilePictureThumbnailUrl || profile.profilePictureUrl;
					if (url) {
						setAvatarUrl(url);
						setImgError(false);
					}
				})
				.catch(() => {});
		}
	}, [userData?.role, userData?.id]);

	// For company users, use the organization logo
	useEffect(() => {
		if (userData?.role === UserType.COMPANY && selectedOrganization?.logoUrl) {
			setAvatarUrl(selectedOrganization.logoUrl);
			setImgError(false);
		}
	}, [userData?.role, selectedOrganization?.logoUrl]);

	// Generate initials for fallback avatar, from the same resolved label as the name (US-AUTH-01).
	const getInitials = () => {
		const initials = displayName(userData, '')
			.split(' ')
			.filter(Boolean)
			.slice(0, 2)
			.map((part) => part.charAt(0).toUpperCase())
			.join('');
		return initials || '?';
	};

	const handleLogout = () => {
		logout();

		if (width < Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE)) {
			setAsideStatus(false);
		}
	};

	return (
		<>
			<div
				className={classNames('user', { open: collapseStatus })}
				role='presentation'
				onClick={() => setCollapseStatus(!collapseStatus)}>
				<div className='user-avatar'>
					{avatarUrl && !imgError ? (
						<img
							src={avatarUrl}
							alt='Avatar'
							width={128}
							height={128}
							style={{ objectFit: 'cover', borderRadius: '50%' }}
							onError={() => setImgError(true)}
						/>
					) : (
						<div
							className='d-flex align-items-center justify-content-center bg-primary text-white fw-bold'
							style={{
								width: 48,
								height: 48,
								borderRadius: '50%',
								fontSize: '1.2rem',
							}}>
							{getInitials()}
						</div>
					)}
				</div>
				<div className='user-info'>
					{/* The identity claims are optional, so interpolating them directly printed
					    "undefined undefined" on every authenticated page (US-AUTH-01). */}
					<div className='user-name d-flex align-items-center text-white'>
						{displayName(userData, t('common:yourAccount'))}
					</div>
					{userData?.position && (
						<div className='user-sub-title'>{userData.position}</div>
					)}
				</div>
			</div>
			{/* <DropdownMenu>
				<DropdownItem>
					<Button
						icon='AccountBox'
						onClick={() =>
							navigate(
								`../${demoPagesMenu.appointment.subMenu.employeeID.path}/${userData?.id}`,
							)
						}>
						Profile
					</Button>
				</DropdownItem>
				<DropdownItem>
					<Button
						icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
						onClick={() => setDarkModeStatus(!darkModeStatus)}
						aria-label='Toggle fullscreen'>
						{darkModeStatus ? 'Dark Mode' : 'Light Mode'}
					</Button>
				</DropdownItem>
			</DropdownMenu> */}

			<Collapse isOpen={collapseStatus} className='user-menu'>
				<nav aria-label='aside-bottom-user-menu'>
					<div className='navigation'>
						{/* Freelance Setting */}
						{/* {userData?.role === UserType.FREELANCE ? (
							<>
								<div
									className='navigation-item cursor-pointer'
									onClick={() =>
										navigate(
											`/talent/settings`,
										)
									}>
									<span className='navigation-link navigation-link-pill'>
										<span className='navigation-link-info'>
											<Icon icon='Settings' className='navigation-icon' />
											<span className='navigation-text'>Settings</span>
										</span>
									</span>
								</div>
								<div
									className='navigation-item cursor-pointer'
									onClick={() =>
										navigate(
											`/talent/settings`
										)
									}>
									<span className='navigation-link navigation-link-pill'>
										<span className='navigation-link-info'>
											<Icon icon='Payment' className='navigation-icon' />
											<span className='navigation-text'>
												Billing Settings
											</span>
										</span>
									</span>
								</div>
							</>
						) : null} */}

						{/* <div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() =>
								navigate(
									`../${demoPagesMenu.appointment.subMenu.employeeID.path}/${userData?.id}`,
									// @ts-ignore
									handleItem(),
								)
							}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon icon='AccountBox' className='navigation-icon' />
									<span className='navigation-text'>
										{t('menu:Profile') as ReactNode}
									</span>
								</span>
							</span>
						</div> */}
						{/* <div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
								setDarkModeStatus(!darkModeStatus);
								handleItem();
							}}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon
										icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
										color={darkModeStatus ? 'info' : 'warning'}
										className='navigation-icon'
									/>
									<span className='navigation-text'>
										{darkModeStatus
											? (t('menu:DarkMode') as ReactNode)
											: (t('menu:LightMode') as ReactNode)}
									</span>
								</span>
							</span>
						</div> */}
					</div>
				</nav>
				<NavigationLine />
				<nav aria-label='aside-bottom-user-menu-2'>
					<div className='navigation'>
						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => (window.location.href = 'mailto:info@opentalents.eu')}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon icon='Email' className='navigation-icon' />
									<span className='navigation-text'>
										{t('menu:Support') as ReactNode}
									</span>
								</span>
							</span>
						</div>
						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={handleLogout}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon icon='Logout' className='navigation-icon' />
									<span className='navigation-text'>
										{t('menu:Logout') as ReactNode}
									</span>
								</span>
							</span>
						</div>
					</div>
				</nav>
			</Collapse>
		</>
	);
};

export default User;
